
import React, { useEffect, useRef } from 'react';
import { Star, Clock, CheckCircle } from 'lucide-react';

// Counter component with animation
const AnimatedCounter = ({ value, label, icon }: { value: string, label: string, icon: React.ReactNode }) => {
  const counterRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (counterRef.current) {
              counterRef.current.classList.add('animate-counter');
              counterRef.current.style.setProperty('--num', value.replace(/\D/g, ''));
            }
          }
        });
      },
      { threshold: 0.5 }
    );
    
    if (counterRef.current) {
      observer.observe(counterRef.current);
    }
    
    return () => {
      if (counterRef.current) {
        observer.unobserve(counterRef.current);
      }
    };
  }, [value]);

  return (
    <div className="bg-white p-6 rounded-lg shadow-md text-center">
      <div className="flex justify-center mb-4">
        {icon}
      </div>
      <div className="counter-box" ref={counterRef}>
        {value}
      </div>
      <p className="mt-2 text-deepshadow font-medium">{label}</p>
    </div>
  );
};

// Testimonial data
const testimonials = [
  {
    id: 1,
    name: 'Jessica Thompson',
    business: 'Bloom Boutique',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=1000',
    quote: 'GoldCrusade delivered exactly what we needed and more. Their AI agent understood our style immediately, and the team had our site up in less than 48 hours. Sales are up 32% since launch!',
    rating: 5,
  },
  {
    id: 2,
    name: 'Marcus Johnson',
    business: 'Elite Security',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=1000',
    quote: 'I was skeptical about getting a "free" website, thinking there would be hidden costs or poor quality. I was completely wrong - the site is professional, fast, and has already generated 27 new leads.',
    rating: 5,
  },
  {
    id: 3,
    name: 'Sophia Chen',
    business: 'Glow Spa & Wellness',
    image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=1000',
    quote: "The KnightAI agent asked all the right questions to understand our brand. The website they created perfectly captures our spa's ambiance. Booking appointments has never been easier!",
    rating: 5,
  },
];

const TestimonialsSection = () => {
  return (
    <section id="testimonials" className="section-container bg-gray-50 py-20">
      <h2 className="section-title">
        We've Built Over <span className="text-goldcore">200</span> Sites — Here's What Clients Say
      </h2>
      
      {/* Stats Counter */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16 max-w-4xl mx-auto">
        <AnimatedCounter 
          value="200+" 
          label="Free Sites Built" 
          icon={<CheckCircle className="h-10 w-10 text-goldcore" />}
        />
        <AnimatedCounter 
          value="48" 
          label="Avg Build Time (hours)" 
          icon={<Clock className="h-10 w-10 text-goldcore" />}
        />
        <AnimatedCounter 
          value="96%" 
          label="Satisfaction Rate" 
          icon={<Star className="h-10 w-10 text-goldcore" />}
        />
      </div>
      
      {/* Testimonials */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
        {testimonials.map((testimonial) => (
          <div key={testimonial.id} className="testimonial-card flex flex-col">
            {/* Rating */}
            <div className="flex mb-4">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={16}
                  className={i < testimonial.rating ? "text-goldcore fill-goldcore" : "text-gray-300"}
                />
              ))}
            </div>
            
            {/* Quote */}
            <p className="italic text-deepshadow/80 mb-6 flex-grow">"{testimonial.quote}"</p>
            
            {/* Client */}
            <div className="flex items-center mt-auto">
              <img
                src={testimonial.image}
                alt={testimonial.name}
                className="h-12 w-12 rounded-full object-cover mr-4"
              />
              <div>
                <p className="font-bold text-knightblack">{testimonial.name}</p>
                <p className="text-sm text-deepshadow/70">{testimonial.business}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default TestimonialsSection;
